/**
 * TransactionDetailSheet — View transaction details + take action
 *
 * Centered hero layout: amount → merchant → date → category badge + attribution →
 * 4 recategorization chips (always visible) → category-specific actions.
 *
 * ART_SUPPLY: "Exclude from materials pre-fill" toggle
 * ART_INCOME: "Match to a piece" or linked artwork display
 * BUSINESS_EXPENSE: chips only
 */

import { useState, useCallback } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { useAuth } from '@/contexts/AuthContext';
import { T } from '@/styles/tokens';
import { BottomSheet } from '@/components/ui/BottomSheet';
import type { ArtTransaction } from '@/hooks/useArtTransactions';
import { MerchantAvatar } from '@/components/money/MerchantAvatar';
import { CategoryIcon } from '@/components/CategoryIcon';

interface TransactionDetailSheetProps {
  transaction: ArtTransaction | null;
  isOpen: boolean;
  onClose: () => void;
  onRecategorize: (txn: ArtTransaction, category: string) => void;
  onLinkIncome?: (txn: ArtTransaction) => void;
  onUnlink?: (txn: ArtTransaction) => void;
  verdictTitleMap?: Record<string, string>;
  /** All transactions for context (unused in this component, passed by callers) */
  allTransactions?: ArtTransaction[];
}

const CATEGORY_LABELS: Record<string, string> = {
  ART_SUPPLY: 'Art Supply',
  ART_INCOME: 'Art Income',
  BUSINESS_EXPENSE: 'Business',
  UNCERTAIN: 'Uncertain',
  PERSONAL: 'Personal',
  IGNORE: 'Ignored',
};

const RECAT_BUTTONS = [
  { key: 'ART_SUPPLY', label: 'Art Supply', color: T.primary },
  { key: 'BUSINESS_EXPENSE', label: 'Business', color: T.text },
  { key: 'ART_INCOME', label: 'Art Income', color: T.primary },
  { key: 'PERSONAL', label: 'Not Art', color: T.textMuted },
];

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getAttribution(txn: ArtTransaction): string {
  if (txn.tpConfirmedBy === 'user') return 'You categorized this';
  if (txn.tpConfirmedBy === 'ai') {
    const pct = txn.tpConfidence != null ? Math.round(txn.tpConfidence * 100) : null;
    const conf = pct != null ? ` \u00B7 ${pct}% confidence` : '';
    return txn.tpConfidence != null && txn.tpConfidence >= 0.8
      ? `AI categorized${conf}`
      : `AI suggested${conf}`;
  }
  if (txn.tpCategory === 'UNCERTAIN') return 'Not yet categorized';
  return '';
}

export function TransactionDetailSheet({
  transaction,
  isOpen,
  onClose,
  onRecategorize,
  onLinkIncome,
  onUnlink,
  verdictTitleMap = {},
}: TransactionDetailSheetProps) {
  const { user } = useAuth();
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [confirmUnlink, setConfirmUnlink] = useState(false);

  const handleClose = useCallback(() => {
    setSelectedKey(null);
    setConfirmUnlink(false);
    onClose();
  }, [onClose]);

  const handlePillSelect = useCallback((category: string) => {
    if (!transaction || selectedKey) return;
    setSelectedKey(category);
    onRecategorize(transaction, category);
    setTimeout(() => {
      setSelectedKey(null);
      onClose();
    }, 300);
  }, [transaction, selectedKey, onRecategorize, onClose]);

  const handleToggleExclude = useCallback(async () => {
    if (!transaction || !user) return;
    const txnRef = doc(db, 'users', user.uid, 'transactions', transaction.id);
    await updateDoc(txnRef, { excludeFromPreFill: !transaction.excludeFromPreFill });
  }, [transaction, user]);

  const handleUnlink = useCallback(() => {
    if (!transaction) return;
    if (!confirmUnlink) {
      setConfirmUnlink(true);
      return;
    }
    onUnlink?.(transaction);
    setConfirmUnlink(false);
    onClose();
  }, [transaction, confirmUnlink, onUnlink, onClose]);

  if (!transaction) return null;

  const amountDollars = Math.round(Math.abs(transaction.amount) / 100);
  const isIncome = transaction.tpCategory === 'ART_INCOME';
  const isArtSupply = transaction.tpCategory === 'ART_SUPPLY';
  const attribution = getAttribution(transaction);
  const linkedTitle = transaction.tpLinkedArtworkId
    ? verdictTitleMap[transaction.tpLinkedArtworkId] || 'Unknown piece'
    : null;

  return (
    <BottomSheet isOpen={isOpen} onClose={handleClose}>
      <div style={{
        padding: '8px 20px',
        paddingBottom: 'max(24px, env(safe-area-inset-bottom, 24px))',
        textAlign: 'center',
      }}>
        {/* Merchant logo */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
          <MerchantAvatar merchantName={transaction.merchantName} logoUrl={transaction.merchantLogoUrl} category={transaction.tpCategory} size={56} />
        </div>

        {/* Hero amount */}
        <div style={{ fontSize: 26, fontWeight: 800, color: isIncome ? T.primary : T.text, lineHeight: 1.1 }}>
          {isIncome ? '+' : '\u2212'}${amountDollars.toLocaleString()}
        </div>

        {/* Merchant */}
        <div style={{ fontSize: 18, fontWeight: 600, color: T.text, marginTop: 8 }}>
          {transaction.merchantName}
        </div>

        {/* Date */}
        <div style={{ fontSize: 13, color: T.textMuted, marginTop: 4 }}>
          {formatDate(transaction.date)}
        </div>

        {/* Divider */}
        <div style={{ height: 1, backgroundColor: T.borderLight, margin: '16px 0' }} />

        {/* Category badge + attribution */}
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8 }}>
          <CategoryIcon category={transaction.tpCategory} size={24} />
          <span style={{
            display: 'inline-block',
            borderRadius: 12,
            background: T.borderLight,
            padding: '4px 12px',
            fontSize: 12,
            fontWeight: 500,
            color: T.textSecondary,
          }}>
            {CATEGORY_LABELS[transaction.tpCategory] || transaction.tpCategory}
          </span>
        </div>

        {attribution && (
          <div style={{ fontSize: 12, color: T.textMuted, fontStyle: 'italic', marginTop: 8 }}>
            {attribution}
          </div>
        )}

        {/* Divider */}
        <div style={{ height: 1, backgroundColor: T.borderLight, margin: '16px 0' }} />

        {/* 4 recategorization chips — always visible */}
        <div style={{
          display: 'flex',
          gap: 8,
          justifyContent: 'center',
          flexWrap: 'wrap',
        }}>
          {RECAT_BUTTONS.map((btn) => {
            const isSelected = selectedKey === btn.key;
            const isCurrent = transaction.tpCategory === btn.key && !selectedKey;
            return (
              <button
                key={btn.key}
                onClick={() => handlePillSelect(btn.key)}
                disabled={!!selectedKey || isCurrent}
                style={{
                  padding: '8px 12px',
                  borderRadius: 20,
                  border: `1.5px solid ${btn.color}`,
                  background: isSelected || isCurrent ? btn.color : 'transparent',
                  color: isSelected || isCurrent ? T.white : btn.color,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: selectedKey || isCurrent ? 'default' : 'pointer',
                  opacity: (selectedKey && !isSelected) ? 0.4 : isCurrent ? 0.6 : 1,
                  transition: 'background 150ms, color 150ms, opacity 150ms',
                }}
              >
                {btn.label}
              </button>
            );
          })}
        </div>

        {/* Category-specific actions */}
        {(isArtSupply || isIncome) && (
          <>
            <div style={{ height: 1, backgroundColor: T.borderLight, margin: '16px 0' }} />

            {/* ART_SUPPLY: Exclude from materials pre-fill toggle */}
            {isArtSupply && (
              <div
                onClick={handleToggleExclude}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  padding: '4px 0',
                }}
              >
                <span style={{ fontSize: 13, color: T.textSecondary }}>
                  Exclude from materials pre-fill
                </span>
                <div style={{
                  width: 40,
                  height: 22,
                  borderRadius: 11,
                  background: transaction.excludeFromPreFill ? T.primary : T.border,
                  padding: 2,
                  transition: 'background 150ms',
                  flexShrink: 0,
                }}>
                  <div style={{
                    width: 18,
                    height: 18,
                    borderRadius: 8,
                    background: T.white,
                    transform: transaction.excludeFromPreFill ? 'translateX(18px)' : 'translateX(0)',
                    transition: 'transform 150ms',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                  }} />
                </div>
              </div>
            )}

            {/* ART_INCOME: Linking action */}
            {isIncome && !linkedTitle && (
              <div
                onClick={() => onLinkIncome?.(transaction)}
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: T.primary,
                  cursor: 'pointer',
                  padding: '4px 0',
                }}
              >
                Match to a piece &rarr;
              </div>
            )}

            {isIncome && linkedTitle && !confirmUnlink && (
              <div
                onClick={handleUnlink}
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: T.primary,
                  cursor: 'pointer',
                  padding: '4px 0',
                }}
              >
                Linked to &ldquo;{linkedTitle}&rdquo;
              </div>
            )}

            {isIncome && linkedTitle && confirmUnlink && (
              <div style={{ padding: '4px 0' }}>
                <div style={{ fontSize: 12, color: T.textSecondary, marginBottom: 8 }}>
                  Unlink from &ldquo;{linkedTitle}&rdquo;? The artwork will revert to Available.
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                  <button
                    onClick={() => setConfirmUnlink(false)}
                    style={{
                      padding: '6px 16px',
                      borderRadius: 16,
                      border: `1px solid ${T.border}`,
                      background: 'transparent',
                      fontSize: 12,
                      fontWeight: 600,
                      color: T.textSecondary,
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUnlink}
                    style={{
                      padding: '6px 16px',
                      borderRadius: 16,
                      border: 'none',
                      background: T.warning,
                      fontSize: 12,
                      fontWeight: 600,
                      color: T.white,
                      cursor: 'pointer',
                    }}
                  >
                    Unlink
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </BottomSheet>
  );
}

export default TransactionDetailSheet;
